/* ---------------------------------------------------------------------------
   Qué ofrece cada fila del listado de Informes en proceso, según su estado.

   Viven acá y no dentro del JSX para poder probarlas como funciones puras: son
   las reglas del proceso comercial, y un test que las recorra estado por estado
   es lo que impide que agregar un estado nuevo al enum deje una fila ofreciendo
   una acción que no corresponde.
--------------------------------------------------------------------------- */

import type { EstadoInforme } from "@/lib/informes";

/**
 * Estados desde los que se puede COTIZAR.
 *
 * Es una lista de permitidos y no de prohibidos, al revés del prototipo
 * `18-08-2026`, que deshabilita el botón solo en tres estados y habilita todo
 * lo demás. Esa forma deja pasar dos casos que el prototipo nunca ve porque su
 * maqueta no los tiene:
 *
 * - `incompleto`: un borrador a medias no tiene qué cotizar. RF-F1 arranca
 *   "DADO un informe en estado Creado", no antes.
 * - `en_espera_revision_final`: el informe ya está escrito y esperando visado;
 *   volver a cotizarlo no significa nada.
 *
 * Con la lista de permitidos, un estado nuevo NACE sin el botón y hay que
 * decidir explícitamente si lo lleva. Al revés, lo hereda sin que nadie lo
 * haya pensado.
 */
const ESTADOS_QUE_COTIZAN: readonly EstadoInforme[] = [
  "creado",
  // RF-G2: mientras la cotización está en revisión todavía se puede editar y
  // regenerar, así que la acción sigue disponible.
  "cotizacion_en_revision",
];

/**
 * Estados en los que se puede registrar a mano la aceptación del cliente.
 *
 * Solo cuando la cotización YA está en manos del cliente: es el atajo para
 * cuando acepta por teléfono o por WhatsApp en vez de usar el enlace del
 * correo (RF-G3 emite ese enlace; esto cubre al cliente que no lo usa).
 */
const ESTADOS_QUE_ACEPTAN: readonly EstadoInforme[] = ["cotizacion_enviada_cliente"];

export function puedeCotizar(estado: EstadoInforme): boolean {
  return ESTADOS_QUE_COTIZAN.includes(estado);
}

export function puedeMarcarAceptada(estado: EstadoInforme): boolean {
  return ESTADOS_QUE_ACEPTAN.includes(estado);
}

/**
 * El botón de la izquierda cambia de nombre, no de destino conceptual: antes
 * de que el cliente acepte se mira o se corrige la solicitud; después, lo que
 * queda es LLENAR el informe, que es otro trabajo y por eso otro rótulo.
 */
export function etiquetaVerEditar(estado: EstadoInforme): string {
  return estado === "en_proceso" ? "Completar informe" : "Ver / Editar";
}
